import { createContext, useContext, ReactNode, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/query-keys';

type BuildPlan = 'free' | 'starter' | 'pro' | 'team' | 'max' | 'admin';

interface BuildStats {
  plan: BuildPlan;
  buildCreditsUsed: number;
  completedBuilds: number;
  hasUsedThirdStart: boolean;
  planLimit: number;
  remainingCredits: number;
  canStartNew: boolean;
  canReceivePromptOnFinish: boolean;
  activeBuildId: string | null;
  scope?: 'user' | 'team';
  teamId?: string | null;
}

interface BuildCreditsContextType {
  stats: BuildStats | null;
  loading: boolean;
  refreshStats: () => Promise<void>;
  startBuild: () => Promise<{ success: boolean; buildId?: string; blocked?: boolean; reason?: string }>;
  finishBuild: (buildId: string, conversationId: string) => Promise<{
    success: boolean;
    canReceivePrompt: boolean;
    blockedReason?: string
  }>;
}

interface BuildStatsPayload {
  plan?: BuildPlan;
  build_credits_used?: number;
  completed_builds?: number;
  has_used_third_start?: boolean;
  plan_limit?: number;
  remaining_credits?: number;
  can_start_new?: boolean;
  can_receive_prompt_on_finish?: boolean;
  active_build?: {
    id?: string | null;
  } | null;
  scope?: 'user' | 'team';
  team_id?: string | null;
}

interface StartBuildPayload {
  blocked?: boolean;
  reason?: string;
  build_id?: string;
  stats?: BuildStatsPayload;
}

interface FinishBuildPayload {
  can_receive_prompt?: boolean;
  blocked_reason?: string;
  stats?: BuildStatsPayload;
}

const defaultStats: BuildStats = {
  plan: 'free',
  buildCreditsUsed: 0,
  completedBuilds: 0,
  hasUsedThirdStart: false,
  planLimit: 2,
  remainingCredits: 2,
  canStartNew: true,
  canReceivePromptOnFinish: true,
  activeBuildId: null,
};

const BuildCreditsContext = createContext<BuildCreditsContextType | undefined>(undefined);

const mapBuildStats = (data: BuildStatsPayload | null | undefined): BuildStats => ({
  plan: data?.plan || 'free',
  buildCreditsUsed: data?.build_credits_used || 0,
  completedBuilds: data?.completed_builds || 0,
  hasUsedThirdStart: data?.has_used_third_start || false,
  planLimit: data?.plan_limit || 2,
  remainingCredits: data?.remaining_credits || 2,
  canStartNew: data?.can_start_new ?? true,
  canReceivePromptOnFinish: data?.can_receive_prompt_on_finish ?? true,
  activeBuildId: data?.active_build?.id || null,
  scope: data?.scope || 'user',
  teamId: data?.team_id || null,
});

const fetchBuildStats = async (accessToken: string): Promise<BuildStats> => {
  try {
    const { data, error } = await supabase.functions.invoke('get-build-stats', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (error) throw error;

    return mapBuildStats(data as BuildStatsPayload);
  } catch (error) {
    console.error('Error fetching build stats:', error);
    return defaultStats;
  }
};

const applyStartBuildStats = (
  previous: BuildStats | undefined,
  data: StartBuildPayload,
): BuildStats | undefined => {
  if (!previous || !data.stats) return previous;

  return {
    ...previous,
    plan: data.stats.plan || previous.plan,
    buildCreditsUsed: data.stats.build_credits_used ?? previous.buildCreditsUsed,
    completedBuilds: data.stats.completed_builds ?? previous.completedBuilds,
    planLimit: data.stats.plan_limit ?? previous.planLimit,
    // Trust the server's computed balance/eligibility; the fallbacks only fire
    // if an older edge function omits the fields.
    remainingCredits: data.stats.remaining_credits
      ?? ((data.stats.plan_limit ?? previous.planLimit) - (data.stats.build_credits_used ?? previous.buildCreditsUsed)),
    canStartNew: data.stats.can_start_new ?? previous.canStartNew,
    activeBuildId: data.build_id ?? previous.activeBuildId,
    scope: data.stats.scope || previous.scope,
    teamId: data.stats.team_id || previous.teamId,
  };
};

const applyFinishBuildStats = (
  previous: BuildStats | undefined,
  data: FinishBuildPayload,
): BuildStats | undefined => {
  if (!previous || !data.stats) return previous;

  const nextPlan = data.stats.plan || previous.plan;
  const buildCreditsUsed = data.stats.build_credits_used ?? previous.buildCreditsUsed;
  const completedBuilds = data.stats.completed_builds ?? previous.completedBuilds;
  const planLimit = data.stats.plan_limit ?? previous.planLimit;

  return {
    ...previous,
    plan: nextPlan,
    buildCreditsUsed,
    completedBuilds,
    planLimit,
    // Trust the server's computed balance/eligibility rather than re-deriving it
    // here — a client-side rule silently diverges if the server logic changes
    // (abuse blocks, grace periods, team-pool draining). Fallbacks are defensive.
    remainingCredits: data.stats.remaining_credits ?? (planLimit - buildCreditsUsed),
    canStartNew: data.stats.can_start_new ?? previous.canStartNew,
    activeBuildId: null,
    scope: data.stats.scope || previous.scope,
    teamId: data.stats.team_id || previous.teamId,
  };
};

export const BuildCreditsProvider = ({ children }: { children: ReactNode }) => {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;
  const accessToken = session?.access_token ?? null;

  const statsQuery = useQuery({
    queryKey: queryKeys.buildCredits(userId),
    queryFn: () => fetchBuildStats(accessToken as string),
    enabled: Boolean(userId && accessToken),
  });

  const refreshStatsMutation = useMutation({
    mutationFn: fetchBuildStats,
    onSuccess: (nextStats) => {
      queryClient.setQueryData(queryKeys.buildCredits(userId), nextStats);
      if (userId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.buildCredits(userId) });
      }
    },
  });

  const startBuildMutation = useMutation({
    mutationFn: async (token: string): Promise<StartBuildPayload> => {
      const { data, error } = await supabase.functions.invoke('start-build', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (error) throw error;
      return data as StartBuildPayload;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<BuildStats>(
        queryKeys.buildCredits(userId),
        (previous) => applyStartBuildStats(previous, data),
      );
      if (userId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.buildCredits(userId) });
      }
    },
  });

  const finishBuildMutation = useMutation({
    mutationFn: async ({
      token,
      buildId,
      conversationId,
    }: {
      token: string;
      buildId: string;
      conversationId: string;
    }): Promise<FinishBuildPayload> => {
      const { data, error } = await supabase.functions.invoke('finish-build', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: { build_id: buildId, conversation_id: conversationId },
      });

      if (error) throw error;
      return data as FinishBuildPayload;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<BuildStats>(
        queryKeys.buildCredits(userId),
        (previous) => applyFinishBuildStats(previous, data),
      );
      if (userId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.buildCredits(userId) });
      }
    },
  });

  const refreshStats = useCallback(async () => {
    if (!accessToken) {
      queryClient.setQueryData(queryKeys.buildCredits(userId), null);
      return;
    }

    await refreshStatsMutation.mutateAsync(accessToken);
  }, [accessToken, queryClient, refreshStatsMutation, userId]);

  const startBuild = useCallback(async (): Promise<{ success: boolean; buildId?: string; blocked?: boolean; reason?: string }> => {
    if (!accessToken) {
      return { success: false, reason: 'not_authenticated' };
    }

    try {
      const data = await startBuildMutation.mutateAsync(accessToken);

      if (data.blocked) {
        return { success: false, blocked: true, reason: data.reason };
      }

      return { success: true, buildId: data.build_id };
    } catch (error) {
      console.error('Error starting build:', error);
      return { success: false, reason: 'error' };
    }
  }, [accessToken, startBuildMutation]);

  const finishBuild = useCallback(async (
    buildId: string,
    conversationId: string,
  ): Promise<{ success: boolean; canReceivePrompt: boolean; blockedReason?: string }> => {
    if (!accessToken) {
      return { success: false, canReceivePrompt: false, blockedReason: 'not_authenticated' };
    }

    try {
      const data = await finishBuildMutation.mutateAsync({ token: accessToken, buildId, conversationId });

      return {
        success: true,
        canReceivePrompt: data.can_receive_prompt ?? false,
        blockedReason: data.blocked_reason,
      };
    } catch (error) {
      console.error('Error finishing build:', error);
      return { success: false, canReceivePrompt: false, blockedReason: 'error' };
    }
  }, [accessToken, finishBuildMutation]);

  return (
    <BuildCreditsContext.Provider
      value={{
        stats: userId ? statsQuery.data ?? null : null,
        loading: Boolean(userId && accessToken && statsQuery.isLoading),
        refreshStats,
        startBuild,
        finishBuild,
      }}
    >
      {children}
    </BuildCreditsContext.Provider>
  );
};

export const useBuildCredits = () => {
  const context = useContext(BuildCreditsContext);
  if (context === undefined) {
    throw new Error('useBuildCredits must be used within a BuildCreditsProvider');
  }
  return context;
};

// Helper function to format credits display
export const formatCreditsDisplay = (stats: BuildStats): string => {
  if (stats.plan === 'free') {
    return `${stats.buildCreditsUsed} / ${stats.planLimit} used`;
  }
  return `${stats.buildCreditsUsed} / ${stats.planLimit} used`;
};

// Helper to get tooltip text
export const getCreditsTooltip = (): string => {
  return "A build costs 1 credit total: 0.5 when you start, and 0.5 when you finish (generate the final prompt). If you start but don't finish, it still uses 0.5.";
};
